<template>
  <div :class="{ expanded: expanded }" class="collapsed-section collapsed-section-bootstrap">
    <div class="collapsed-section-expander">
      <button
        :class="expanded ? 'btn btn-primary-minus' : 'btn btn-primary-plus'"
        @click="toggle"
        type="button"
      >
        {{ expanded ? "Hide" : "Show" }}
      </button>
    </div>
    <div class="collapsed-section-inner">
      <div class="collapsed-section-text-above">
        <slot name="text-above"></slot>
      </div>
    </div>

    <div class="clearfix"></div>
    <div ref="expander" class="collapsed-section-text-below">
      <slot name="text-below"></slot>
      <div v-observe-visibility="onVisibilityChanged" class="end-tracker"></div>
      <div v-if="props.showDoneButton" class="text-center">
        <button class="btn btn-primary" @click="done" type="button">Done</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeMount } from "vue";
import { debug as d } from "debug";
import { ObserveVisibility as vObserveVisibility } from "vue3-observe-visibility";
import { Analytics } from "../../services/analytics/analytics";

const debug = d("cs.CollapsedSectionComponent");

const props = withDefaults(
  defineProps<{
    showDoneButton: boolean;
  }>(),
  {
    showDoneButton: false,
  },
);

const expanded = ref(false);
const expander = ref<HTMLElement | null>(null);

const jExpander = computed(() => $(expander.value as HTMLElement));

const slideDownOptions = {
  duration: 1000,
  easing: "swing",
};

const slideUpOptions = {
  duration: 500,
  easing: "swing",
};

const refExpander = ref<HTMLElement | null>(null);

const expand = () => {
  if (!expanded.value) {
    expanded.value = true;
    debug("Expanding");
    Analytics.action("Expanded Section", "SectionActions", {
      collapsedSectionTitle: refExpander.value?.querySelector(".collapsed-section-text-above")
        ?.textContent,
    });
    jExpander.value?.stop();
    jExpander.value?.slideDown(slideDownOptions);
  }
};

const collapse = () => {
  if (expanded.value) {
    expanded.value = false;
    debug("Collapsing");
    Analytics.action("Collapsed Section", "SectionActions", {
      collapsedSectionTitle: refExpander.value?.querySelector(".collapsed-section-text-above")
        ?.textContent,
    });

    const expanderHeight = refExpander.value?.clientHeight || 0;
    jExpander.value.stop();
    jExpander.value.slideUp(slideUpOptions);
    const pageElement = refExpander.value?.closest(".page-content");
    if (pageElement) {
      const originalScrollTop = pageElement.scrollTop;
      $(pageElement).animate(
        { scrollTop: originalScrollTop - expanderHeight },
        slideUpOptions.duration,
      );
    }
  }
};

const toggle = () => {
  if (!expanded.value) {
    expand();
  } else {
    collapse();
  }
};

const done = () => {
  collapse();
};

onMounted(() => {
  debug("Running mounted");
  jExpander.value.slideUp(0);
  debug("Finished mounted handler");
});

onBeforeMount(() => {
  debug("beforeDestroy");
});

const onVisibilityChanged = () => {
  // Nothing to do
};
</script>

<style lang="less">
@gray-lighter: #f5f6f7;

.collapsed-section {
  margin: 0.6em -15px;
  padding: 10px 20px;
  background-color: @gray-lighter;
  position: relative;

  .end-tracker {
    display: block;
    width: 1px;
    height: 1px;
    pointer-events: none;
    font-size: 0px;
    line-height: 0;
    margin: 0;
    padding: 0;
    position: relative;
    top: -50px;
  }

  .collapsed-section-text-above {
    margin: 0;

    p:first-child {
      font-weight: bold;
    }
  }

  .collapsed-section-toggle-button {
    display: inline-block;
  }

  .collapsed-section-content {
    border-top: 1px solid transparent; // Address issue with collapsing margins making jQuery slide jerky
  }

  .collapsed-section-expander {
    margin-top: 10px;
  }
}

.collapsed-section.collapsed-section-bootstrap {
  padding: 10px 15px 10px 20px;
  border-radius: 4px;

  .collapsed-section-text-above {
    margin-top: 10px;
    margin-bottom: 0px;

    h4:first-child {
      line-height: 1.42857143; //Same as "p";
      margin: 0 0 10px; //Same as "p".
    }
  }

  .collapsed-section-expander {
    float: right;

    margin-top: 5px;
    margin-right: 0px;
    margin-bottom: 5px;
    margin-left: 10px;
  }
}
</style>
